import { Component, ElementRef, EventEmitter, HostListener, Input, Output, inject } from "@angular/core";
import { type TagPreset } from "src/app/shared/constants/tag-presets";

import { TaskUrgency } from "../domains/task";

/**
 * Task Priority Picker - Toggle binaire Normale/Urgente
 * Design moderne style toggle (comme task-visibility-toggle)
 *
 * Usage:
 * <app-task-priority-picker
 *   [(priority)]="task.urgency"
 *   [showLabel]="true"
 * ></app-task-priority-picker>
 */
@Component({
  selector: "app-task-priority-picker",
  templateUrl: "./task-priority-picker.component.html",
  styleUrls: ["./task-priority-picker.component.scss"],
})
export class TaskPriorityPickerComponent {
  /** Valeur sélectionnée */
  @Input() priority: TaskUrgency = TaskUrgency.NORMAL;
  /** Mode désactivé */
  @Input() disabled: boolean = false;
  /** Afficher le label (ou juste l'icône) */
  @Input() showLabel: boolean = true;
  /** Taille compacte */
  @Input() compact: boolean = false;

  /** Two-way binding */
  @Output() priorityChange = new EventEmitter<TaskUrgency>();

  private elementRef = inject(ElementRef);

  /** Toggle entre Normale et Urgente */
  toggle(): void {
    if (this.disabled) return;
    this.priority = this.priority === TaskUrgency.URGENT ? TaskUrgency.NORMAL : TaskUrgency.URGENT;
    this.priorityChange.emit(this.priority);
    // Le bouton est remplacé par l'étiquette (et inversement) : garder le focus pour continuer au clavier
    setTimeout(() => this.elementRef.nativeElement.querySelector("eva-tag, button")?.focus());
  }

  /** Gestion du clavier */
  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggle();
    }
  }

  /** Couleur du drapeau selon la priorité */
  getPriorityColor(): string {
    return this.priority === TaskUrgency.URGENT ? "var(--red-500)" : "var(--blue-500)";
  }

  /** Label court pour l'affichage */
  get displayLabel(): string {
    return this.priority === TaskUrgency.URGENT ? "Urgent" : "Normale";
  }

  /** Label pour le tooltip */
  get tooltipLabel(): string {
    return this.priority === TaskUrgency.URGENT ? "Priorité urgente" : "Priorité normale";
  }

  /** Vrai si priorité urgente */
  get isUrgent(): boolean {
    return this.priority === TaskUrgency.URGENT;
  }

  get urgentTagPreset(): TagPreset {
    return { label: "Urgent", severity: "danger", icon: "heroFlag" };
  }
}
